import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { api, fmtINR } from "@/lib/api";
import { formatCareerRoi, isCareerRoiEstimate } from "@/lib/careerRoi";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import UniversityImage from "@/components/UniversityImage";
import { Sparkles, TrendingUp, ArrowRight, ShieldCheck, Target, Mountain, Star, FileText } from "lucide-react";

// Visual chrome per band — NOT data. Universities and band assignment come from
// the canonical /recommendations/top-picks engine.
export const BAND_CHROME = {
  safe:   { label: "Safe",   icon: ShieldCheck, dot: "bg-emerald-500", text: "text-[var(--success)]", soft: "bg-emerald-50",  border: "border-emerald-200" },
  target: { label: "Target", icon: Target,      dot: "bg-amber-500",   text: "text-[var(--warning)]",   soft: "bg-amber-50",    border: "border-amber-200" },
  reach:  { label: "Reach",  icon: Mountain,    dot: "bg-rose-500",    text: "text-[var(--danger)]",    soft: "bg-rose-50",     border: "border-rose-200" },
};

const VISA_PCT_BY_COUNTRY = { AU: 90, UK: 86, US: 78, DE: 92, NL: 90, CA: 88, IE: 86, SG: 82, FR: 78, SE: 84, NZ: 86 };

/**
 * Compose a "report" object from a canonical university record + its live financial plan.
 * All values are derived — none are hardcoded.
 */
export function reportFor(uni, band, plan) {
  if (!uni) return null;
  const decisionScore = Math.round(uni.fit_score ?? 0);
  const visaPct = VISA_PCT_BY_COUNTRY[uni.country_code] ?? 80;
  const visaReadiness = visaPct >= 88 ? "Strong" : visaPct >= 80 ? "High" : "Moderate";
  return {
    slug: uni.slug,
    name: uni.name,
    short: uni.name.replace(/^The /, "").replace(/ University.*$/, " University"),
    city: uni.city || "",
    country: uni.country,
    band,
    bandChrome: BAND_CHROME[band],
    rank: uni.rank_in_band ?? 1,
    decisionScore,
    matchScore: decisionScore,
    fit_score: uni.fit_score ?? 0,
    band_rationale: uni.band_rationale,
    satisfaction: Math.min(5, 3.6 + (uni.employment_rate_pct || 80) / 100),
    tuitionLabel: fmtINR(uni.tuition_per_year_inr || 0),
    livingLabel: fmtINR(uni.living_per_year_inr || 0),
    acceptance: uni.acceptance_rate_pct,
    ibMedian: uni.ib_score_median,
    employment: uni.employment_rate_pct,
    visaReadiness,
    visaPct,
    plan,
    // raw record for any drill-down screens
    uni,
  };
}

export default function DecisionReport() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [picks, setPicks] = useState(null);
  const [plans, setPlans] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Canonical ranking — works for guests (DEMO_PROFILE) and signed-in users.
        const { data } = await api.get("/recommendations/top-picks");
        if (cancelled) return;
        setPicks(data);
        // For Career ROI we need each pick's grand_total + median_graduate_salary_inr
        // — fetched from /financial-plan so it uses the SAME inputs as the Financial screen.
        const top = [data.safe?.[0], data.target?.[0], data.reach?.[0]].filter(Boolean);
        const planResults = await Promise.all(top.map(u =>
          api.post("/financial-plan", { university_slug: u.slug, duration_years: 3, inflation_pct: 5 })
            .then(res => [u.slug, res.data]).catch(() => [u.slug, null])
        ));
        if (cancelled) return;
        setPlans(Object.fromEntries(planResults.filter(([, v]) => v)));
      } catch (e) {
        setError(e?.message || "Unable to load decision report");
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const reports = useMemo(() => {
    if (!picks) return [];
    return [
      reportFor(picks.safe?.[0], "safe", plans[picks.safe?.[0]?.slug]),
      reportFor(picks.target?.[0], "target", plans[picks.target?.[0]?.slug]),
      reportFor(picks.reach?.[0], "reach", plans[picks.reach?.[0]?.slug]),
    ].filter(Boolean);
  }, [picks, plans]);

  return (
    <div className="min-h-screen bg-white lg:pl-[240px]">
      <Nav />
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-start justify-between gap-6 flex-wrap mb-3">
          <div className="eyebrow inline-flex items-center gap-1.5">
            <Sparkles size={12} className="text-[var(--ind)]" strokeWidth={2.2} />
            <span>Final Verdict · Unimora AI Decision Report</span>
          </div>
          <button
            onClick={() => nav("/decision-report/one")}
            data-testid="open-one-decision-report"
            className="btn-primary inline-flex items-center gap-2"
          >
            <FileText size={14} strokeWidth={2} /> One Decision Report
          </button>
        </div>
        <h1 className="font-semibold text-[var(--s900)] text-h1 md:text-display leading-none">
          Your decision, <em className="italic text-[var(--ind)]">explained.</em>
        </h1>
        <p className="text-body text-[var(--s600)] mt-4 max-w-2xl leading-relaxed">
          One evidence-backed verdict per band — Safe, Target, Reach — drawn directly from your Decision Engine. Every score and trade-off comes from verified data.
          {!user && <span className="block mt-2 text-[var(--s500)]">Sample reports — sign in to generate your personalised ADR.</span>}
        </p>

        {/* 3 cards */}
        {error && <div className="mt-10 text-[var(--s500)]">{error}</div>}
        {!picks && !error && <div className="mt-10 text-[var(--s500)]" data-testid="dr-loading">Loading your decision report…</div>}
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          {reports.map(r => (
            <ReportCard key={r.slug} r={r} onOpen={() => nav(`/decision-report/${r.slug}`)} />
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}

function ReportCard({ r, onOpen }) {
  const Chrome = r.bandChrome;
  const BandIcon = Chrome.icon;
  const careerRoiInputs = {
    medianGraduateSalaryInr: r.plan?.university?.median_graduate_salary_inr,
    totalInvestmentInr: r.plan?.grand_total,
  };
  const careerRoiLabel = formatCareerRoi(careerRoiInputs);
  const careerRoiNumeric = parseFloat(careerRoiLabel);
  const careerRoiPct = isNaN(careerRoiNumeric) ? 0 : Math.min(100, careerRoiNumeric * 18);
  return (
    <button
      onClick={onOpen}
      data-testid={`adr-card-${r.slug}`}
      className="relative text-left rounded-[var(--r20)] border border-[var(--s200)] bg-white shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all group overflow-hidden"
    >
      {/* Top accent bar — band-coloured signal stripe */}
      <span
        aria-hidden
        data-testid={`adr-accent-${r.slug}`}
        className={`absolute top-0 left-0 right-0 h-1 z-10 ${Chrome.dot}`}
      />
      {/* Official campus image / branded fallback */}
      <UniversityImage
        slug={r.slug}
        name={r.name}
        aspectClass="aspect-[21/9]"
        rounded=""
        showGradientOverlay
      />
      <div className="p-6">
      <div className="flex items-start justify-between gap-3 mb-5">
        <div className="flex items-center gap-2.5">
          <span className={`w-9 h-9 rounded-full ${Chrome.soft} ${Chrome.text} flex items-center justify-center`}>
            <BandIcon size={16} strokeWidth={1.9} />
          </span>
          <div>
            <div className={`text-micro font-semibold tracking-[0.05em] uppercase ${Chrome.text}`} data-testid={`adr-band-${r.slug}`}>{Chrome.label} pick</div>
            <div className="text-micro text-[var(--s500)] mt-0.5">#{r.rank} in band</div>
          </div>
        </div>
        <span className={`text-micro font-semibold tracking-[0.05em] uppercase ${Chrome.text} ${Chrome.soft} border ${Chrome.border} px-2 py-1 rounded-[var(--r8)] inline-flex items-center gap-1`}>
          <TrendingUp size={10} strokeWidth={2.4} /> Live match
        </span>
      </div>

      <div className="text-h3 font-semibold text-[var(--s900)] leading-tight">{r.name}</div>
      <div className="text-label text-[var(--s500)] mt-1">{r.city}{r.city && " · "}{r.country}</div>

      <UnimoraRecommendation value={r.decisionScore} slug={r.slug} />

      <div className="grid grid-cols-2 gap-3 mt-4">
        <Metric label="AI Match Score" value={`${r.matchScore}%`} pct={r.matchScore} accent="emerald" testid={`adr-fit-${r.slug}`} />
        <Metric label="Acceptance" value={`${r.acceptance ?? "—"}%`} pct={r.acceptance ?? 0} />
        <Metric label="Tuition / yr" value={r.tuitionLabel} pct={Math.max(20, 100 - 8 * (r.uni.tuition_per_year_inr / 100000))} testid={`adr-tuition-${r.slug}`} />
        <Metric label="Living / yr" value={r.livingLabel} pct={Math.max(30, 100 - 4 * (r.uni.living_per_year_inr / 100000))} testid={`adr-living-${r.slug}`} />
        <Metric label="Career ROI" value={careerRoiLabel} pct={careerRoiPct} accent="emerald" estimate={isCareerRoiEstimate(careerRoiInputs)} testid={`adr-career-roi-${r.slug}`} />
        <Metric label="Visa Readiness" value={r.visaReadiness} pct={r.visaPct} accent="emerald" />
      </div>

      <div className="mt-5 inline-flex items-center gap-1.5 text-label font-medium text-[var(--ind)] group-hover:translate-x-1 transition-transform">
        View full report <ArrowRight size={13} strokeWidth={2.2} />
      </div>
      </div>
    </button>
  );
}

function UnimoraRecommendation({ value, slug }) {
  const tier =
    value >= 90 ? "Highly Recommended"
    : value >= 80 ? "Recommended"
    : value >= 70 ? "Worth Considering"
    : value >= 60 ? "Stretch Pick"
    : "Use With Caution";
  const stars =
    value >= 90 ? 5
    : value >= 80 ? 4
    : value >= 70 ? 3
    : value >= 60 ? 2
    : 1;
  return (
    <div className="mt-5 rounded-[var(--r20)] bg-[var(--ind-soft)] p-4" data-testid={`unimora-recommendation-${slug}`}>
      <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--ind)]">Unimora AI Recommendation</div>
      <div className="mt-1.5 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-body-lg font-semibold text-[var(--s900)] leading-tight" data-testid={`recommendation-tier-${slug}`}>{tier}</div>
        <div className="flex items-center gap-0.5" aria-label={`${stars} out of 5 stars`}>
          {[1, 2, 3, 4, 5].map(i => (
            <Star key={i} size={14} strokeWidth={2}
              className={i <= stars ? "fill-[var(--ind)] text-[var(--ind)]" : "text-[var(--s300)]"} />
          ))}
        </div>
      </div>
      <div className="mt-2 text-label text-[var(--s600)]">
        Decision Score · <span className="font-semibold text-[var(--s900)]" data-testid={`decision-score-${slug}`}>{value}/100</span>
      </div>
    </div>
  );
}

function Metric({ label, value, pct, accent, estimate, testid }) {
  const valueClass = accent === "emerald" ? "text-[var(--success)]" : "text-[var(--s900)]";
  return (
    <div className="rounded-[var(--r12)] border border-[var(--s200)] p-3.5 bg-white" {...(testid ? { "data-testid": testid } : {})}>
      <div className="text-micro text-[var(--s500)] leading-snug flex items-center gap-1.5">
        {label}
        {estimate && <span className="text-micro tracking-[0.05em] uppercase text-[var(--ind)] font-semibold">EST.</span>}
      </div>
      <div className={`text-h3 font-semibold mt-1 ${valueClass}`}>{value}</div>
      <div className="mt-2 h-1.5 rounded-full bg-[var(--s100)] overflow-hidden">
        <div className="h-full bg-[var(--ind)] rounded-full" style={{ width: `${Math.max(8, Math.min(100, pct))}%` }} />
      </div>
    </div>
  );
}
